import React, { useState, useEffect } from 'react';
import './LoadingScreen.css';

interface LoadingScreenProps {
    onFinish?: () => void;
    minDuration?: number;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onFinish, minDuration = 2000 }) => {
    const [fadeOut, setFadeOut] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFadeOut(true);
            setTimeout(() => onFinish?.(), 500);
        }, minDuration);
        return () => clearTimeout(timer);
    }, [minDuration, onFinish]);

    return (
        <div className={`loading-screen-overlay ${fadeOut ? 'fade-out' : ''}`}>
            <div className="loading-content">
                <div className="mascot-svg-container">
                    <svg viewBox="0 0 400 400" className="mascot-svg">
                        <defs>
                            <filter id="glow">
                                <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                                <feMerge>
                                    <feMergeNode in="coloredBlur" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </defs>

                        {/* Wings */}
                        <g className="wings-group">
                            <path className="wing-left" d="M160 220 Q100 240 80 200 Q80 160 160 180 Z" fill="white" />
                            <path className="wing-right" d="M240 220 Q300 240 320 200 Q320 160 240 180 Z" fill="white" />
                        </g>

                        {/* Main Body */}
                        <circle cx="200" cy="220" r="95" fill="#A5E1FF" stroke="#68A9D1" strokeWidth="3" />

                        {/* Ears */}
                        <path d="M135 150 Q120 100 160 135" fill="#A5E1FF" stroke="#68A9D1" strokeWidth="3" strokeLinecap="round" />
                        <path d="M265 150 Q280 100 240 135" fill="#A5E1FF" stroke="#68A9D1" strokeWidth="3" strokeLinecap="round" />

                        {/* Crown */}
                        <g transform="translate(165, 85) scale(0.7)">
                            <path d="M0 40 L10 10 L30 30 L50 0 L70 30 L90 10 L100 40 Z" fill="#FFD700" stroke="#B8860B" strokeWidth="3" />
                            <circle cx="10" cy="10" r="6" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
                            <circle cx="50" cy="0" r="6" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
                            <circle cx="90" cy="10" r="6" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
                            <circle cx="50" cy="25" r="5" fill="#FF4D4D" />
                        </g>

                        {/* Face */}
                        <g>
                            {/* Cheeks */}
                            <ellipse cx="160" cy="205" rx="15" ry="8" fill="#FFB7B7" opacity="0.6" />
                            <ellipse cx="240" cy="205" rx="15" ry="8" fill="#FFB7B7" opacity="0.6" />

                            {/* Winking eye */}
                            <path d="M230 180 Q245 170 260 185" fill="none" stroke="#4A3428" strokeWidth="4" strokeLinecap="round" />
                            {/* Open eye with blink */}
                            <circle className="eye-blink" cx="170" cy="180" r="8" fill="#4A3428" />

                            {/* Mouth & Nose */}
                            <circle cx="200" cy="195" r="6" fill="#4A3428" />
                            <path d="M190 205 Q200 215 210 205" fill="none" stroke="#4A3428" strokeWidth="3" strokeLinecap="round" />
                            <path d="M198 205 L198 215" stroke="#4A3428" strokeWidth="2" />
                        </g>

                        {/* Skirt */}
                        <path d="M110 280 Q200 320 290 280 L280 260 Q200 280 120 260 Z" fill="#90CC80" stroke="#609050" strokeWidth="2" />

                        {/* Left Arm & Coin */}
                        <g>
                            <path d="M130 220 Q90 200 100 180" fill="none" stroke="#A5E1FF" strokeWidth="20" strokeLinecap="round" />
                            <g className="coin-rotate">
                                <circle cx="100" cy="185" r="25" fill="#FFD700" stroke="#B8860B" strokeWidth="2" filter="url(#glow)" />
                                <text x="100" y="195" textAnchor="middle" fill="#B8860B" fontSize="24" fontWeight="bold">฿</text>
                            </g>
                        </g>

                        {/* Right Arm & Phone */}
                        <g>
                            <path d="M270 220 Q310 200 300 180" fill="none" stroke="#A5E1FF" strokeWidth="20" strokeLinecap="round" />
                            <rect x="290" y="160" width="40" height="70" rx="5" fill="#A594F9" stroke="#6D5FC0" strokeWidth="2" transform="rotate(5, 310, 195)" />
                            <rect x="295" y="165" width="30" height="50" rx="2" fill="#FFF9C4" transform="rotate(5, 310, 195)" />
                            <text x="312" y="200" textAnchor="middle" fill="#FBC02D" fontSize="18" fontWeight="bold" transform="rotate(5, 312, 195)">฿</text>
                            <rect x="305" y="220" width="10" height="4" rx="1" fill="#6D5FC0" transform="rotate(5, 310, 195)" />
                        </g>

                        {/* Feet */}
                        <path d="M170 310 Q175 340 190 335" fill="none" stroke="#A5E1FF" strokeWidth="15" strokeLinecap="round" />
                        <path d="M230 310 Q225 340 210 335" fill="none" stroke="#A5E1FF" strokeWidth="15" strokeLinecap="round" />
                    </svg>
                </div>
            </div>

            <div className="loading-branding">
                <h1 className="loading-title">ตังค์ตุง</h1>
                <p className="loading-subtitle">ผู้ช่วยคิดเงินร้านค้า</p>
            </div>
        </div>
    );
};

export default LoadingScreen;
